import React, { useMemo } from 'react';
import * as yup from 'yup';
import { SchemaOf } from 'yup';
import {
    FIELD_LABELS,
    getDateFromString,
    getStringFromDate,
    shortLabel,
    transformToNumber,
} from '../../../../../../helpers';
import {
    ActivityEntityName,
    useUpsertOrderProductionMutation,
} from '../../../../../../services';
import { useHistorialTab } from '../../../../historial';
import {
    DAuditInfo,
    IAuditStamps,
    RhDateInput,
    RhFormWrapper,
    RhTabsWrapper,
    RHTextField,
} from '../../../../../dum';
import { BranchInput } from './branch/branch-input';
import { OrderProductionEmployeesColumns } from './order-production-employees/order-production-employees-columns';
import { OrderProductionProductsColumns } from './order-production-products/order-production-products-columns';
import { OrderProductionTypeInput } from './order-production-type/order-production-type-input';
import { doesEmployeeBelongToBranch } from './validation/does-employee-belong-to-branch';
import { doesEmployeeBelongToOrderProductionType } from './validation/does-employee-belong-to-order-production-type';
import { doesEmployeeHaveUpStatus } from './validation/does-employee-have-up-status';
import { doesMachineBelongToBranch } from './validation/does-machine-belong-to-branch';
import { doesMachineBelongToOrderProductionType } from './validation/does-machine-belong-to-order-production-type';
import { doesProductBelongToOrderProductionType } from './validation/does-product-belong-to-order-production-type';
import { isEmployeeUnique } from './validation/is-employee-unique';
import { isMachineProductUnique } from './validation/is-machine-product-unique';
import { isThereOnlyOneEmployeeLeader } from './validation/is-there-only-one-employee-leader';
import { IEmployee } from '../employee/employee-form';
import { OrderProductionProductsConsumedColumns } from './order-production-products-consumed/order-production-products-consumed-columns';
import {
    performanceTab,
    SavedProductionSnapshot,
} from '../../../../production-performance';

export interface IOrderProduction {
    id?: number | null;
    start_date: string;
    branch_id?: number | null;
    order_production_type_id?: number | null;
    waste?: number;
    shift?: number | null;
    order_production_products: {
        // Carried from the dialog query through to the mutation so the service
        // can match an existing row instead of deleting and recreating it.
        id?: number | null;
        kilos: number;
        machine?: {
            id: number;
            name: string;
            branch_id?: number | null;
        } | null;
        product?: {
            id: number;
            compound_description: string;
            current_group_weight?: number | null;
        } | null;
        groups?: number | null;
        group_weight?: number | null;
        hours?: number | null;
    }[];
    order_production_products_consumed: {
        id?: number | null;
        kilos: number;
        machine?: {
            id: number;
            name: string;
            branch_id?: number | null;
        } | null;
        product?: {
            id: number;
            compound_description: string;
            current_group_weight?: number | null;
        } | null;
        groups?: number | null;
        group_weight?: number | null;
        hours?: number | null;
    }[];
    order_production_employees: {
        employee?: {
            id: number;
            fullname: string;
            order_production_type_id?: number | null;
        } | null;
        is_leader: boolean | number;
    }[];
}

// Audit stamps ride alongside the form values — display only, never submitted,
// and deliberately kept out of IOrderProduction (see IAuditStamps).
interface TOrderProduction extends IOrderProduction, IAuditStamps {
    id?: number | null;
}

interface IOrderProductionForm {
    orderProduction?: TOrderProduction;
    onSuccess: () => void;
    disabled?: boolean;
}

export const ID: keyof IEmployee = 'id';

export const OrderProductionForm = ({
    orderProduction,
    onSuccess,
    disabled = false,
}: IOrderProductionForm) => {
    const [mutation, { loading }] = useUpsertOrderProductionMutation();

    const historialTab = useHistorialTab({
        entityName: ActivityEntityName.OrderProduction,
        entityId: orderProduction?.id,
        unsavedMessage:
            'El historial aparece una vez que la producción ha sido guardada.',
        emptyMessage: 'No hay actividades registradas para esta producción.',
    });

    // What the database already holds for this production. The Rendimiento tab
    // grades what is being TYPED, but the baseline has to exclude what was
    // SAVED — on an edit those are different numbers, and subtracting the typed
    // ones would leave part of this run inside its own average. Undefined for a
    // new production, which the aggregate cannot contain at all.
    const savedSnapshot = useMemo<SavedProductionSnapshot | undefined>(() => {
        if (!orderProduction?.id) return undefined;
        return {
            startDate: getDateFromString(orderProduction.start_date),
            waste: orderProduction.waste ?? 0,
            lines: orderProduction.order_production_products.flatMap((line) =>
                line.machine?.id && line.product?.id
                    ? [
                          {
                              machineId: line.machine.id,
                              productId: line.product.id,
                              kilos: line.kilos,
                              groups: line.groups ?? 0,
                              hours: line.hours ?? null,
                          },
                      ]
                    : [],
            ),
            consumedLines:
                orderProduction.order_production_products_consumed.flatMap(
                    (line) =>
                        line.machine?.id
                            ? [
                                  {
                                      machineId: line.machine.id,
                                      kilos: line.kilos,
                                      hours: line.hours ?? null,
                                  },
                              ]
                            : [],
                ),
        };
    }, [orderProduction]);

    const initialValues = useMemo<IOrderProduction>(() => {
        return {
            id: orderProduction?.id || undefined,
            start_date: orderProduction
                ? getDateFromString(orderProduction.start_date)
                : '',
            order_production_type_id:
                orderProduction?.order_production_type_id || null,
            waste:
                orderProduction &&
                orderProduction.waste !== undefined &&
                orderProduction?.waste >= 0
                    ? orderProduction.waste
                    : 0,
            shift:
                orderProduction &&
                !!orderProduction.shift &&
                orderProduction?.shift >= 0
                    ? orderProduction.shift
                    : 0,
            branch_id: orderProduction ? orderProduction.branch_id : null,
            order_production_products:
                orderProduction?.order_production_products || [
                    {
                        group_weight: 0,
                        groups: 0,
                        kilos: 0,
                        machine: null,
                        product: null,
                        hours: 8,
                    },
                ],
            order_production_products_consumed:
                orderProduction?.order_production_products_consumed || [
                    {
                        group_weight: 0,
                        groups: 0,
                        kilos: 0,
                        machine: null,
                        product: null,
                        hours: 8,
                    },
                ],
            order_production_employees:
                orderProduction?.order_production_employees.map((employee) => {
                    return {
                        ...employee,
                        is_leader: employee.is_leader === 1,
                    };
                }) || [],
        };
    }, [orderProduction]);

    const validationSchema: SchemaOf<IOrderProduction> = yup.object({
        id: yup.number().notRequired().nullable(),
        start_date: yup.string().required(`'Fecha' es obligatorio`).nullable(),
        branch_id: yup
            .number()
            .required(`'Sucursal' es obligatorio`)
            .nullable(),
        order_production_type_id: yup
            .number()
            .required(`'Orden de producción' es obligatorio`)
            .nullable(),
        waste: yup
            .number()
            .transform(transformToNumber)
            .nullable()
            .required(`'Deperdicio' es obligatorio`),
        order_production_products: yup
            .array()
            .of(
                yup.object().shape({
                    id: yup.number().notRequired().nullable(),
                    machine: yup
                        .mixed()
                        .required(`'Máquina' es obligatorio`)
                        .nullable()
                        .test(
                            'is-machine-employee-unique',
                            isMachineProductUnique,
                        )
                        .test(
                            'does-machine-belong-to-branch',
                            doesMachineBelongToBranch,
                        )
                        .test(
                            'does-machine-belong-to-order-production-type',
                            doesMachineBelongToOrderProductionType,
                        ),
                    product: yup
                        .mixed()
                        .required(`'Producto' es obligatorio`)
                        .nullable()
                        .test(
                            'is-machine-employee-unique',
                            isMachineProductUnique,
                        )
                        .test(
                            'does-product-belong-to-order-production-type',
                            doesProductBelongToOrderProductionType,
                        ),
                    kilos: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .required(`'Kilos' es obligatorio`),
                    groups: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .required(`'Grupos' es obligatorio`),
                    group_weight: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .required(`'Peso del grupo' es obligatorio`),
                    hours: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .notRequired(),
                }),
            )
            .required(`'Productos' es obligatorio`),
        order_production_products_consumed: yup
            .array()
            .of(
                yup.object().shape({
                    id: yup.number().notRequired().nullable(),
                    machine: yup
                        .mixed()
                        .required(`'Máquina' es obligatorio`)
                        .nullable(),
                    product: yup
                        .mixed()
                        .required(`'Producto' es obligatorio`)
                        .nullable(),
                    kilos: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .required(`'Kilos' es obligatorio`),
                    groups: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .required(`'Grupos' es obligatorio`),
                    group_weight: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .required(`'Peso del grupo' es obligatorio`),
                    hours: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .notRequired(),
                }),
            )
            .required(`'Productos consumidos' es obligatorio`),

        shift: yup
            .number()
            .transform(transformToNumber)
            .nullable()
            .required(`'Turno' es obligatorio`),
        order_production_employees: yup
            .array()
            .of(
                yup.object().shape({
                    employee: yup
                        .mixed()
                        .required(`'Empleado' es obligatorio`)
                        .nullable()
                        .test('is-employee-unique', isEmployeeUnique)
                        .test(
                            'does-employee-belong-to-order-production-type',
                            doesEmployeeBelongToOrderProductionType,
                        )
                        .test(
                            'does-employee-belong-to-branch',
                            doesEmployeeBelongToBranch,
                        )
                        .test(
                            'does-employee-have-up-status',
                            doesEmployeeHaveUpStatus,
                        ),

                    is_leader: yup
                        .mixed()
                        .notRequired()
                        .test(
                            'is-there-only-one-employee-leader',
                            isThereOnlyOneEmployeeLeader,
                        ),
                }),
            )
            .required(`'Empleado' es obligatorio`),
    });

    return (
        <RhFormWrapper
            validationSchema={validationSchema}
            initialValues={initialValues}
            disabled={loading || disabled}
            actionBarStart={
                orderProduction?.id ? (
                    <DAuditInfo
                        created_by={orderProduction.created_by}
                        updated_by={orderProduction.updated_by}
                        createdLabel={'Creada por'}
                        updatedLabel={'Actualizada por'}
                    />
                ) : null
            }
            onSubmit={async (data) => {
                try {
                    await mutation({
                        variables: {
                            OrderProductionInput: {
                                id: orderProduction?.id || null,
                                start_date: getStringFromDate(data.start_date),
                                branch_id: data?.branch_id || null,
                                order_production_type_id:
                                    data?.order_production_type_id || null,
                                waste: data?.waste || 0,
                                shift:
                                    !!data?.shift && data?.shift > 0
                                        ? data.shift
                                        : null,
                                order_production_products:
                                    data.order_production_products.map(
                                        (productionProduct) => {
                                            return {
                                                id:
                                                    productionProduct.id ??
                                                    null,
                                                kilos: productionProduct.kilos,
                                                product_id:
                                                    productionProduct.product!
                                                        .id,
                                                machine_id:
                                                    productionProduct.machine!
                                                        .id,
                                                group_weight:
                                                    productionProduct.group_weight!,
                                                groups:
                                                    productionProduct.groups ||
                                                    0,
                                                hours: productionProduct.hours,
                                            };
                                        },
                                    ),
                                order_production_products_consumed:
                                    data.order_production_products_consumed.map(
                                        (productionResource) => {
                                            return {
                                                id:
                                                    productionResource.id ??
                                                    null,
                                                kilos: productionResource.kilos,
                                                product_id:
                                                    productionResource.product!
                                                        .id,
                                                machine_id:
                                                    productionResource.machine!
                                                        .id,
                                                group_weight:
                                                    productionResource.group_weight!,
                                                groups:
                                                    productionResource.groups ||
                                                    0,
                                                hours: productionResource.hours,
                                            };
                                        },
                                    ),
                                order_production_employees:
                                    data.order_production_employees.map(
                                        (employee) => {
                                            return {
                                                employee_id:
                                                    employee.employee!.id,
                                                is_leader: employee.is_leader
                                                    ? 1
                                                    : 0,
                                            };
                                        },
                                    ),
                            },
                        },
                    });
                    onSuccess();
                } catch (e) {
                    console.error(e);
                }
            }}
            onInvalid={async (errors) => {
                console.error(errors);
            }}
        >
            <RhTabsWrapper
                tabs={[
                    {
                        label: 'General',
                        fields: [
                            'start_date',
                            'branch_id',
                            'order_production_type_id',
                            'waste',
                            'shift',
                        ],
                        content: (
                            <>
                                <RhDateInput
                                    name={'start_date'}
                                    label={shortLabel(FIELD_LABELS.start_date)}
                                />
                                <BranchInput />
                                <OrderProductionTypeInput />
                                <RHTextField
                                    name={'waste'}
                                    label={shortLabel(FIELD_LABELS.waste)}
                                />
                                <RHTextField
                                    name={'shift'}
                                    label={shortLabel(FIELD_LABELS.shift)}
                                />
                            </>
                        ),
                    },
                    {
                        label: 'Productos',
                        fields: ['order_production_products'],
                        content: <OrderProductionProductsColumns />,
                    },
                    {
                        label: 'Productos consumidos',
                        fields: ['order_production_products_consumed'],
                        content: <OrderProductionProductsConsumedColumns />,
                    },
                    {
                        label: 'Empleados',
                        fields: ['order_production_employees'],
                        content: <OrderProductionEmployeesColumns />,
                    },
                    // Read-only, and disabled until there is a line to grade.
                    ...performanceTab(savedSnapshot),
                    // Empty for non-admins, so they get no tab at all.
                    ...historialTab,
                ]}
            />
        </RhFormWrapper>
    );
};
